import { Button, Grid2 as Grid } from '@mui/material';
import { Stack } from '@mui/system';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { ItemsByBoard, setHasMoved } from '../../../redux/slices/board';
import { SearchFilter } from '../../searchinput';
import { IBoardView } from '../types';
import { Board } from './board';
import { BoardViewFilter } from '../../boardViewFilter';
import { SelectBoxOption } from '../../../interfaces';
import { Add } from '@mui/icons-material';
import { CurrentView } from './currentView';

export const BoardView: React.FC<IBoardView> = ({
    searchDetails,
    fetchBoardItems,
    boards,
    getTitle,
    entityType,
    onUpdateItemBoard,
    stack = false,
    refetchBoards,
    actions,
    onClick,
    createBtnName,
    createBtnProps,
    creatable,
    onCreateBtn,
    tagFilterOption,
}) => {

    const itemsByBoard = useAppSelector(store => store.board?.itemsByBoard);
    const dispatch = useAppDispatch();

    const [filterTags, setFilterTags] = React.useState<SelectBoxOption[]>([]);

    const handleDragEnd = async (result: typeof DropResult, itemsByBoard?: ItemsByBoard) => {
        if (!result.destination) return;

        const { source, destination, draggableId } = result;

        const item = itemsByBoard?.[source?.droppableId]?.find(d => d?._id === draggableId);

        // Only call update if the destination is different from the source
        if ((source.droppableId !== destination.droppableId || source.index !== destination.index) && item) {
            dispatch(setHasMoved(false));
            await onUpdateItemBoard(result, item)
                .then((res) => {
                    if (res.hasOwnProperty('error')) {
                        throw res.error;
                    } else {
                        dispatch(setHasMoved(true));
                    }
                })
                .catch((error) => {
                    enqueueSnackbar(error?.data?.title ?? error?.data ?? `Oops! Something went wrong unable to move ${item.title}.`, { variant: "error" })
                });
        }
    };

    return (
        <DragDropContext onDragEnd={(result: typeof DropResult) => handleDragEnd(result, itemsByBoard)}>
            <Stack id="board-view" spacing={2} height="100%">
                <Grid container spacing={1}>
                    <Grid size={{
                        lg: searchDetails ? 9 : 12,
                        md: !stack ? 9 : 12,
                        sm: 12,
                        xs: 12
                    }}>
                        <Stack gap={1} sx={{ flexDirection: { lg: "row", md: "row", sm: "column", xs: "column" } }} justifyContent={"space-between"}>
                            <CurrentView entity={entityType} />

                            <Stack sx={{ flexDirection: { lg: "row", md: "row", sm: "row", xs: "column" } }} gap={1}>
                                {tagFilterOption && <BoardViewFilter entity={entityType} scope={tagFilterOption.scope} onFilterApply={(tags) => {
                                    setFilterTags(tags);
                                }} />}
                                {creatable && <Button variant="contained" size="small" startIcon={<Add />} onClick={(e) => onCreateBtn?.(e)} {...createBtnProps}>{createBtnName}</Button>}
                            </Stack>
                        </Stack>
                    </Grid>

                    {searchDetails && (
                        <Grid
                            justifyContent="flex-end"
                            size={{
                                lg: 3,
                                md: stack ? 12 : 3,
                                sm: 12,
                                xs: 12
                            }}
                        >
                            <SearchFilter
                                searchPlaceholder={searchDetails.searchPlaceholder ?? ''}
                                handleChange={searchDetails.onSearch}
                                value={searchDetails.search}
                            />
                        </Grid>
                    )}
                </Grid>

                <Stack
                    id="boards"
                    sx={{ overflowY: "hidden" }}
                    direction="row"
                    spacing={2}
                    height="-webkit-fill-available"
                    width="100%"
                >
                    {boards?.map((board) => (
                        <Board
                            actions={actions}
                            refetchBoards={refetchBoards}
                            key={board.id}
                            getTitle={getTitle}
                            entityType={entityType}
                            board={board}
                            onClick={onClick}
                            filters={{ tags: filterTags }}
                            fetchBoardItems={fetchBoardItems}
                            searchDetails={searchDetails}
                        />
                    ))}
                </Stack>
            </Stack>
        </DragDropContext>
    );
};